import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CListGroup,
  CListGroupItem,
  CSpinner,
  CStepper,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableFoot,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilCheckCircle, cilExternalLink } from "@coreui/icons";
import { useQueryClient } from "@tanstack/react-query";
import type { ApiError } from "../../../api/client";
import { useFailure } from "../../../context/useFailure";
import {
  useCartStore,
  type AddOnCartItem,
  type StrategyCartItem,
  type MembershipCartItem,
} from "../../../state/cartStore";
import { beginPendingCheckout } from "../../../state/pendingCheckout";
import {
  customerHasActiveSubscription,
  MY_SUBSCRIPTIONS_QUERY_KEY,
  useMySubscriptions,
  useCheckoutCart,
  useCheckoutStatus,
} from "../../../api/hooks/useStrategySubscriptions";
import {
  isIncludedInMembership,
  MY_MEMBERSHIPS_QUERY_KEY,
  useMyMemberships,
} from "../../../api/hooks/useMemberships";
import { addOnDetailsPath, membershipDetailsPath, routePaths } from "../../../routes/route-paths";
import { ADD_ONS_QUERY_KEY } from "../../../api/hooks/useAddOns";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { stripePromise } from "../../../lib/stripe";
import { formatPriceUsd } from "../../../lib/money";
import { usableOffers, useUsableOffers } from "../../../api/hooks/useOffers";
import { savingOnItem } from "../../../lib/offer-savings";
import { includedByMembershipsInCart } from "../../../lib/cart-overlap";
import { useMembershipTiers } from "../../../api/hooks/useMembershipTiers";
import { useTradingStrategies, type BillingCycle } from "../../../api/hooks/useTradingStrategies";
import { StrategyMetaPills } from "../components/StrategyMetaPills";
import { StrategyTickers } from "../components/StrategyTickers";
import { StrategySideLabel } from "../components/StrategySideLabel";
import { UpgradeCheckoutWizard } from "./UpgradeCheckoutWizard";
import { ExtendCheckoutWizard } from "./ExtendCheckoutWizard";

const BILLING_CYCLES: BillingCycle[] = ["Monthly", "Yearly"];

// CStepper's exposed imperative API. Mirrors the shape used by the End
// Subscription wizard and BackOffice onboarding so the wizard patterns
// stay aligned across the app.
interface CStepperRef {
  next: () => void;
  prev: () => void;
  finish: () => void;
  reset: () => void;
}

interface SubscribeOutcome {
  id: string;
  name: string;
  kind: "Membership" | "AddOn" | "Strategy";
  ok: boolean;
  message: string;
}

/**
 * The checkout page. An upgrade or an extension is checked out on its own, with its own review and
 * outcome, so what the cart holds when the page opens picks the wizard. Only then: paying empties the
 * cart, and the wizard's Done step still has to show.
 */
export function CheckoutWizardCoreui() {
  const upgrade = useCartStore((s) => s.upgrade);
  const extension = useCartStore((s) => s.extension);
  const [kind] = useState(upgrade !== null ? "upgrade" : extension !== null ? "extend" : "buy");

  if (kind === "upgrade") {
    return <UpgradeCheckoutWizard />;
  }

  return kind === "extend" ? <ExtendCheckoutWizard /> : <BuyCheckoutWizard />;
}

function BuyCheckoutWizard() {
  const navigate = useNavigate();
  const strategyItems = useCartStore((s) => s.strategyItems);
  const membershipItems = useCartStore((s) => s.membershipItems);
  const addOnItems = useCartStore((s) => s.addOnItems);
  const clearCart = useCartStore((s) => s.clear);
  const setCheckoutLocked = useCartStore((s) => s.setCheckoutLocked);

  // Only for what the review lines say about each strategy — the order itself is priced off the
  // cart, which carries its own name and price.
  const { data: strategies } = useTradingStrategies();
  const { t } = useTranslation("tradingStrategies");
  const subscriptions = useMySubscriptions();
  const offers = usableOffers(useUsableOffers().data);
  const heldSubscriptions = subscriptions.data ?? [];
  const memberships = useMyMemberships();
  const heldMemberships = useMemo(
    () => memberships.data ?? [],
    [memberships.data]
  );
  const membershipTiers = useMembershipTiers();
  const createCheckoutSession = useCheckoutCart();
  const queryClient = useQueryClient();

  const stepperRef = useRef<CStepperRef>(null);
  const { showFailure, clearFailure } = useFailure();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  // Stripe's figure for this basket, fetched when the session opens. Null until then, and null after
  // if Stripe could not be asked.
  const [amountDueToday, setAmountDueToday] = useState<number | null>(null);
  const [creatingSession, setCreatingSession] = useState(false);
  const [results, setResults] = useState<SubscribeOutcome[]>([]);
  // Snapshot of what's being bought, taken when the Stripe session opens, so
  // the Done step summary survives the cart being cleared on completion.
  const [purchasedStrategies, setPurchasedStrategies] = useState<StrategyCartItem[]>([]);
  const [purchasedMemberships, setPurchasedMemberships] = useState<MembershipCartItem[]>([]);
  const [purchasedAddOns, setPurchasedAddOns] = useState<AddOnCartItem[]>([]);
  // How many subscriptions My strategies should end up holding: what the customer already had
  // when the session opened, plus what they're buying. Captured here rather than on completion
  // because by then the list has been invalidated and may already have grown, which would
  // overcount. Only written to the pending marker once payment actually completes.
  const [expectedSubscriptionCount, setExpectedSubscriptionCount] = useState(0);

  const checkoutStarted = clientSecret !== null;
  const orderStrategies = checkoutStarted ? purchasedStrategies : strategyItems;
  const orderMemberships = checkoutStarted ? purchasedMemberships : membershipItems;
  const orderAddOns = checkoutStarted ? purchasedAddOns : addOnItems;

  // Freeze the cart for exactly as long as a session is open. The cleanup
  // covers leaving the page mid-payment — the session dies with this component,
  // so the lock must too, or the customer is left unable to edit their cart.
  useEffect(() => {
    setCheckoutLocked(checkoutStarted);
    return () => setCheckoutLocked(false);
  }, [checkoutStarted, setCheckoutLocked]);

  const lineSaving = useCallback(
    (item: StrategyCartItem) => savingOnItem(item, offers),
    [offers]
  );

  const offersTotal = useMemo(
    () => orderStrategies.reduce((sum, item) => sum + lineSaving(item), 0),
    [lineSaving, orderStrategies]
  );

  // What the basket costs on each cycle once everything is running, one total per cycle: monthly and
  // yearly lines added together made a figure the customer is never charged. Deliberately not "due
  // today": a line joining a running cycle is prorated to its next renewal, and by how much only
  // Stripe knows, since it prorates by the second and prorates the offer with it. That figure
  // arrives with the setup session.
  const cycleTotals = useMemo(
    () =>
      BILLING_CYCLES.flatMap((cycle) => {
        const strategies = orderStrategies.filter((item) => item.billingCycle === cycle);
        const memberships = orderMemberships.filter((item) => item.billingCycle === cycle);
        const addOns = orderAddOns.filter((item) => item.billingCycle === cycle);
        if (strategies.length + memberships.length + addOns.length === 0) {
          return [];
        }

        const fullTotal =
          strategies.reduce((sum, item) => sum + item.priceUsd, 0) +
          memberships.reduce((sum, item) => sum + item.priceUsd, 0) +
          addOns.reduce((sum, item) => sum + item.priceUsd, 0);
        const total = fullTotal - strategies.reduce((sum, item) => sum + lineSaving(item), 0);
        return [{ cycle, total, fullTotal }];
      }),
    [lineSaving, orderStrategies, orderMemberships, orderAddOns]
  );

  // The full price, not the offer price: an offer runs out, and what follows today's payment is what the
  // customer pays once it has.
  const thenEachCycle = cycleTotals
    .map(({ cycle, fullTotal }) => `${formatPriceUsd(fullTotal)} a ${cycle === "Yearly" ? "year" : "month"}`)
    .join(" and ");
  const linesWithAnOffer = orderStrategies.filter((item) => lineSaving(item) > 0).length;

  const nameWithSide = (item: StrategyCartItem) => {
    const direction = strategies?.find((s) => s.id === item.strategyId)?.direction;
    return direction ? `${item.name} (${t(`sides.${direction}`)})` : item.name;
  };

  const alreadySubscribedItems = useMemo(
    () =>
      orderStrategies.filter((item) =>
        customerHasActiveSubscription(item.strategyId, heldSubscriptions)
      ),
    [orderStrategies, heldSubscriptions]
  );
  const includedInMembershipItems = useMemo(
    () =>
      orderStrategies.filter((item) =>
        isIncludedInMembership(item.strategyId, heldMemberships)
      ),
    [orderStrategies, heldMemberships]
  );
  const includedInCartMembershipItems = useMemo(() => {
    const includedBy = includedByMembershipsInCart(orderMemberships, membershipTiers.data);
    return orderStrategies.filter((item) => includedBy.has(item.strategyId));
  }, [orderStrategies, orderMemberships, membershipTiers.data]);
  const canProceed =
    !subscriptions.isLoading &&
    !subscriptions.error &&
    !memberships.isLoading &&
    !memberships.error &&
    !membershipTiers.isLoading &&
    alreadySubscribedItems.length === 0 &&
    includedInMembershipItems.length === 0 &&
    includedInCartMembershipItems.length === 0;

  const handleBackFromReview = () => navigate(routePaths.cart);

  // Leaving Payment abandons the session, which unlocks the order so Review is
  // editable again. Returning to Payment opens a fresh session for the new cart.
  const handleBackFromPayment = () => {
    setClientSecret(null);
    stepperRef.current?.prev();
  };

  // Opens a Stripe embedded setup session for the cart, then advances to the Payment
  // step where its client secret mounts the card form. Nothing is charged or provisioned
  // here — subscriptions are created off the completion webhook (row-on-payment).
  const handleProceedToPayment = async () => {
    if (!canProceed) {
      showFailure("Please resolve the subscription checks before paying.");
      return;
    }

    setCreatingSession(true);
    clearFailure();
    try {
      const session = await createCheckoutSession.mutateAsync({
        strategies: strategyItems.map((item) => ({
          tradingStrategyId: item.strategyId,
          billingCycle: item.billingCycle,
          offerId: item.offerId ?? null,
        })),
        memberships: membershipItems.map((item) => ({
          membershipTierId: item.tierId,
          billingCycle: item.billingCycle,
        })),
        addOns: addOnItems.map((item) => ({
          addOnId: item.addOnId,
          billingCycle: item.billingCycle,
        })),
      });
      setAmountDueToday(session.amountDueTodayUsd);
      setPurchasedStrategies(strategyItems);
      setPurchasedMemberships(membershipItems);
      setPurchasedAddOns(addOnItems);
      // Memberships and add-ons make no row on My strategies, so only the strategies bought are waited for.
      setExpectedSubscriptionCount(heldSubscriptions.length + strategyItems.length);
      setClientSecret(session.clientSecret);
      setSessionId(session.sessionId);
      stepperRef.current?.next();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "We couldn't start checkout. Please try again.";
      // The alert sits under the button they just pressed, so a toast saying the same thing twice
      // adds nothing but a second place to look.
      showFailure(message, (err as ApiError)?.traceId);
    } finally {
      setCreatingSession(false);
    }
  };

  // Stripe fires this once the card is saved. Access lands when payment settles
  // (webhook -> subscription -> invoice.paid).
  const handlePaymentComplete = useCallback(() => {
    setResults([
      ...purchasedMemberships.map((item) => ({
        id: item.tierId,
        name: item.name,
        kind: "Membership" as const,
        ok: true,
        message: "Confirmed",
      })),
      ...purchasedAddOns.map((item) => ({
        id: item.addOnId,
        name: item.name,
        kind: "AddOn" as const,
        ok: true,
        message: "Confirmed",
      })),
      ...purchasedStrategies.map((item) => ({
        id: item.strategyId,
        name: item.name,
        kind: "Strategy" as const,
        ok: true,
        message: "Confirmed",
      })),
    ]);
    // The real moment to drop the cached list — this is payment completing, not the session
    // opening. It can't win the race with the webhook on its own, which is why the wait below
    // picks up where this leaves off.
    queryClient.invalidateQueries({ queryKey: MY_SUBSCRIPTIONS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: MY_MEMBERSHIPS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ADD_ONS_QUERY_KEY });
    // Entitlement lands several hops later (checkout webhook -> Stripe subscription ->
    // invoice.paid), so the customer would otherwise arrive at a list that hasn't caught up. This
    // tells My strategies what a complete list looks like, so it waits for the rows rather than
    // telling someone who has just paid that they own nothing.
    if (sessionId !== null) {
      beginPendingCheckout(expectedSubscriptionCount, sessionId);
    }
    clearCart();
    stepperRef.current?.next();
  }, [
    clearCart,
    expectedSubscriptionCount,
    purchasedStrategies,
    purchasedMemberships,
    purchasedAddOns,
    queryClient,
    sessionId,
  ]);

  const addOnOnly =
    purchasedAddOns.length > 0 &&
    purchasedStrategies.length === 0 &&
    purchasedMemberships.length === 0;

  // Asked once the card form completes: the payment itself is taken afterwards, off the page.
  const checkoutStatus = useCheckoutStatus(results.length > 0 ? sessionId : null);
  const checkoutDeclined = checkoutStatus.data?.status === "PaymentDeclined";

  const embeddedOptions = useMemo(
    () =>
      clientSecret ? { clientSecret, onComplete: handlePaymentComplete } : null,
    [clientSecret, handlePaymentComplete]
  );

  // Empty-cart guard. Must sit below every hook above: returning before them
  // changes the hook count between renders once the cart empties, which is what
  // crashed this page. Once a session is open the wizard runs off the snapshot,
  // so emptying the cart no longer tears down payment mid-flow.
  if (!checkoutStarted && strategyItems.length === 0 && membershipItems.length === 0 && addOnItems.length === 0) {
    return (
      <div>
        <CAlert color="info">
          Your cart is empty.{" "}
          <Link to={routePaths.strategies}>Browse strategies</Link>.
        </CAlert>
      </div>
    );
  }

  const steps = [
    {
      label: "Review",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Review your order</h4>
          </CCardHeader>
          <CCardBody>
            <p className="text-body-secondary">
              Confirm what you're subscribing to.
            </p>

            {subscriptions.error && (
              <CAlert color="danger">
                We couldn't check your existing subscriptions. Please try
                again before continuing.
              </CAlert>
            )}

            {alreadySubscribedItems.length > 0 && (
              <CAlert color="warning">
                You've already got an active subscription for{" "}
                <strong>
                  {alreadySubscribedItems.map(nameWithSide).join(", ")}
                </strong>
                . Remove {alreadySubscribedItems.length === 1 ? "it" : "them"}{" "}
                from your cart before checking out.
              </CAlert>
            )}

            {includedInMembershipItems.length > 0 && (
              <CAlert color="warning">
                <strong>
                  {includedInMembershipItems.map(nameWithSide).join(", ")}
                </strong>{" "}
                {includedInMembershipItems.length === 1 ? "is" : "are"} already
                included in your membership. Remove{" "}
                {includedInMembershipItems.length === 1 ? "it" : "them"} from your
                cart before checking out.
              </CAlert>
            )}

            {includedInCartMembershipItems.length > 0 && (
              <CAlert color="warning">
                <strong>
                  {includedInCartMembershipItems.map(nameWithSide).join(", ")}
                </strong>{" "}
                {includedInCartMembershipItems.length === 1 ? "is" : "are"} included
                in a membership in your cart. Remove{" "}
                {includedInCartMembershipItems.length === 1 ? "it" : "them"} from your
                cart before checking out.
              </CAlert>
            )}

            <CTable hover responsive className="mb-4">
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell>Item</CTableHeaderCell>
                  <CTableHeaderCell>Billing</CTableHeaderCell>
                  <CTableHeaderCell>Offer</CTableHeaderCell>
                  <CTableHeaderCell className="text-end">
                    Price
                  </CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {orderMemberships.map((item) => (
                  <CTableRow key={item.tierId}>
                    <CTableDataCell>
                      <Link
                        to={membershipDetailsPath(item.tierId)}
                        target="_blank"
                        rel="noreferrer"
                        className="fw-semibold"
                      >
                        {item.name}
                        <CIcon icon={cilExternalLink} size="sm" className="ms-2 opacity-75" />
                      </Link>
                      <div className="small text-body-secondary mt-1">Membership</div>
                    </CTableDataCell>
                    <CTableDataCell>
                      {item.billingCycle === "Yearly" ? "Yearly" : "Monthly"}
                    </CTableDataCell>
                    <CTableDataCell>
                      <span className="text-body-secondary">—</span>
                    </CTableDataCell>
                    <CTableDataCell className="text-end">
                      {formatPriceUsd(item.priceUsd)}
                      {item.billingCycle === "Yearly" ? "/yr" : "/mo"}
                    </CTableDataCell>
                  </CTableRow>
                ))}
                {orderAddOns.map((item) => (
                  <CTableRow key={item.addOnId}>
                    <CTableDataCell>
                      <Link
                        to={addOnDetailsPath(item.addOnId)}
                        target="_blank"
                        rel="noreferrer"
                        className="fw-semibold"
                      >
                        {item.name}
                        <CIcon icon={cilExternalLink} size="sm" className="ms-2 opacity-75" />
                      </Link>
                      <div className="small text-body-secondary mt-1">Add-on</div>
                    </CTableDataCell>
                    <CTableDataCell>
                      {item.billingCycle === "Yearly" ? "Yearly" : "Monthly"}
                    </CTableDataCell>
                    <CTableDataCell>
                      <span className="text-body-secondary">—</span>
                    </CTableDataCell>
                    <CTableDataCell className="text-end">
                      {formatPriceUsd(item.priceUsd)}
                      {item.billingCycle === "Yearly" ? "/yr" : "/mo"}
                    </CTableDataCell>
                  </CTableRow>
                ))}
                {orderStrategies.map((item) => {
                  const strategy = strategies?.find(
                    (s) => s.id === item.strategyId
                  );
                  const offer = offers.find((o) => o.id === item.offerId);
                  return (
                    <CTableRow key={item.strategyId}>
                      <CTableDataCell>
                        {/* A new tab rather than a navigation: leaving this page abandons the
                            Stripe session, and someone checking what they are buying should not
                            have to start paying again. */}
                        <Link
                          to={`/strategies/${item.strategyId}`}
                          target="_blank"
                          rel="noreferrer"
                          className="fw-semibold"
                        >
                          {item.name}
                          <CIcon
                            icon={cilExternalLink}
                            size="sm"
                            className="ms-2 opacity-75"
                          />
                        </Link>
                        {strategy && (
                          <>
                            <div className="mt-1">
                              <StrategySideLabel direction={strategy.direction} className="small fw-semibold" />
                            </div>
                            <div className="mt-1">
                              <StrategyTickers strategy={strategy} />
                            </div>
                            <div className="d-flex flex-wrap gap-1 mt-2">
                              <StrategyMetaPills
                                strategy={strategy}
                                withRiskProfile
                              />
                            </div>
                          </>
                        )}
                      </CTableDataCell>
                      <CTableDataCell>
                        {item.billingCycle === "Yearly" ? "Yearly" : "Monthly"}
                      </CTableDataCell>
                      <CTableDataCell>
                        {offer ? (
                          <>
                            <div className="fw-semibold">{offer.publicName}</div>
                            <small className="text-body-secondary">
                              {offer.customerWording}
                            </small>
                          </>
                        ) : (
                          <span className="text-body-secondary">—</span>
                        )}
                      </CTableDataCell>
                      <CTableDataCell className="text-end">
                        {lineSaving(item) > 0 && (
                          <div className="text-body-secondary text-decoration-line-through small">
                            {formatPriceUsd(item.priceUsd)}
                          </div>
                        )}
                        {formatPriceUsd(item.priceUsd - lineSaving(item))}
                        {item.billingCycle === "Yearly" ? "/yr" : "/mo"}
                      </CTableDataCell>
                    </CTableRow>
                  );
                })}
              </CTableBody>
              {/* The foot, not the last body row: Offers totals the order, and among the strategy rows
                  it read as the discount on whichever strategy happened to be listed last. */}
              <CTableFoot>
                {offersTotal > 0 && (
                  <CTableRow>
                    <CTableDataCell colSpan={3}>Offers</CTableDataCell>
                    <CTableDataCell className="text-end text-success fw-semibold">
                      -{formatPriceUsd(offersTotal)}
                    </CTableDataCell>
                  </CTableRow>
                )}
                {cycleTotals.map(({ cycle, total }) => (
                  <CTableRow key={cycle}>
                    <CTableDataCell colSpan={3}>
                      <strong>{cycleTotals.length > 1 ? cycle : "Order total"}</strong>
                      <div
                        className="text-body-secondary"
                        style={{ fontSize: "0.75rem" }}
                      >
                        {cycle === "Yearly"
                          ? "Charged today, then once a year"
                          : "Part of this month today, then on the 1st"}
                      </div>
                    </CTableDataCell>
                    <CTableDataCell className="text-end">
                      <strong>
                        {formatPriceUsd(total)}
                        {cycle === "Yearly" ? "/yr" : "/mo"}
                      </strong>
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableFoot>
            </CTable>

            {orderStrategies.length > 0 && (
              <p className="text-body-secondary mb-0">
                Signals for these strategies will appear in your portal and by
                email.
              </p>
            )}

            <div className="d-flex justify-content-between mt-4">
              <CButton
                color="secondary"
                variant="outline"
                onClick={handleBackFromReview}
              >
                Back to cart
              </CButton>
              <CButton
                color="primary"
                onClick={handleProceedToPayment}
                disabled={!canProceed || creatingSession}
              >
                {creatingSession && <CSpinner size="sm" className="me-2" />}
                Next: Payment
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: "Payment",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Payment</h4>
          </CCardHeader>
          <CCardBody>
            {/* Stripe's own figure, not ours. Where it could not be asked the page says nothing
                rather than the basket total, which is the number this replaces for being wrong. */}
            {amountDueToday !== null && (
              <p className="text-body-secondary">
                Due today: <strong>{formatPriceUsd(amountDueToday)}</strong>
                {cycleTotals.length > 0 && <> — then {thenEachCycle}</>}
                {linesWithAnOffer === 1 && <>, less your offer while it lasts</>}
                {linesWithAnOffer > 1 && <>, less your offers while they last</>}
              </p>
            )}
            <p className="text-body-secondary">
              Your cart is locked while this payment is open. Use{" "}
              <strong>Back</strong> if you need to change it.
            </p>
            {embeddedOptions ? (
              <div className="mb-3">
                <EmbeddedCheckoutProvider
                  stripe={stripePromise}
                  options={embeddedOptions}
                >
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </div>
            ) : (
              <div className="text-center py-5 text-body-secondary">
                <CSpinner size="sm" className="me-2" />
                Preparing secure checkout…
              </div>
            )}
            <div className="d-flex justify-content-start">
              <CButton
                color="secondary"
                variant="outline"
                onClick={handleBackFromPayment}
              >
                Back
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: "Done",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">{checkoutDeclined ? "Payment declined" : "All set"}</h4>
          </CCardHeader>
          <CCardBody>
            {checkoutDeclined ? (
              <div className="text-center py-3 mb-2">
                <h3 className="mb-2">Your card was declined.</h3>
                <p
                  className="text-body-secondary mx-auto"
                  style={{ maxWidth: 480 }}
                >
                  Nothing was charged for what it couldn't pay for, and that
                  hasn't been added. Add it to your cart again and pay with
                  another card.
                </p>
              </div>
            ) : (
            <>
            <div className="text-center py-3 mb-2">
              <CIcon
                icon={cilCheckCircle}
                size="3xl"
                className="text-success mb-3"
              />
              <h3 className="mb-2">Happy trading.</h3>
              <p
                className="text-body-secondary mx-auto"
                style={{ maxWidth: 480 }}
              >
                {purchasedStrategies.length > 0 &&
                  (purchasedAddOns.length > 0
                    ? "Your strategies and add-on start as soon as the payment clears. Your strategies' signals will come to your portal and your inbox. "
                    : "Your strategies start as soon as the payment clears. Their signals will come to your portal and your inbox. ")}
                {purchasedMemberships.length > 0 &&
                  "Your membership starts as soon as the payment clears. "}
                {purchasedAddOns.length > 0 &&
                  purchasedStrategies.length === 0 &&
                  (purchasedAddOns.length === 1
                    ? "Your add-on starts as soon as the payment clears."
                    : "Your add-ons start as soon as the payment clears.")}
              </p>
            </div>

            {results.some((r) => !r.ok) && (
              <div className="tinted-warning rounded p-3 mb-3">
                A few subscriptions hit a snag. Our team will pick these up —
                check{" "}
                <Link to={routePaths.myStrategies}>My strategies</Link>{" "}
                later today, and contact support if anything's still pending
                tomorrow.
              </div>
            )}

            {results.length > 0 && (
              <CListGroup className="mb-4">
                {results.map((r) => {
                  // A strategy is named by its side, which says what was bought better than "Strategy".
                  const direction =
                    r.kind === "Strategy" ? strategies?.find((s) => s.id === r.id)?.direction : undefined;
                  return (
                    <CListGroupItem
                      key={r.id}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <span className="d-flex align-items-center gap-2">
                        {r.ok && (
                          <CIcon
                            icon={cilCheckCircle}
                            className="text-success"
                          />
                        )}
                        <strong>{r.name}</strong>
                        {direction ? (
                          <StrategySideLabel direction={direction} className="small" />
                        ) : (
                          <span className="small text-body-secondary">{r.kind === "AddOn" ? "Add-on" : r.kind}</span>
                        )}
                      </span>
                      {!r.ok && (
                        <CBadge color="warning" shape="rounded-pill">
                          {r.message}
                        </CBadge>
                      )}
                    </CListGroupItem>
                  );
                })}
              </CListGroup>
            )}
            </>
            )}

            {addOnOnly ? (
              <div className="d-flex flex-wrap gap-2 justify-content-center">
                <Link
                  to={addOnDetailsPath(purchasedAddOns[0].addOnId)}
                  className="btn btn-primary"
                >
                  View your add-on
                </Link>
                <Link
                  to={routePaths.myStrategies}
                  className="btn btn-outline-secondary"
                >
                  My strategies
                </Link>
              </div>
            ) : (
              <div className="d-flex flex-wrap gap-2 justify-content-center">
                <Link
                  to={routePaths.myStrategies}
                  className="btn btn-primary"
                >
                  View my strategies
                </Link>
                <Link
                  to={routePaths.strategies}
                  className="btn btn-outline-secondary"
                >
                  Browse more strategies
                </Link>
              </div>
            )}
          </CCardBody>
        </CCard>
      ),
    },
  ];

  return (
    <>
      <h2>Checkout</h2>
      <p className="text-body-secondary">
        Review your order and complete payment.
      </p>
      <hr />

      <CStepper ref={stepperRef} steps={steps} />
    </>
  );
}
